import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useReactTable, getCoreRowModel, flexRender, createColumnHelper } from '@tanstack/react-table';
import { useMutation, useQuery } from '@apollo/client';
import * as XLSX from 'xlsx';
import axios from 'axios';
import studentofsubjectsQueries from '../../../graphql/queries/studentofsubjects'; // Assuming this query can fetch all student enrollments with answers

import {
  Box,
  Flex,
  Heading,
  Button,
  IconButton,
  HStack,
  useColorModeValue,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronLeft, Trash2 } from 'lucide-react';
import { exportStudentProgressToExcel, ZOOM_IMPORT_SHEET_NAME } from './exportStudentProgressToExcel';
import QuizSurveyDetailTable from './QuizSurveyDetailTable'; // Import the new component
import importZoomProgressMutation from '../../../graphql/mutations/importZoomProgress';
import removeProgressMutation from '../../../graphql/mutations/removeProgress';
import studentUsersQuery from '../../../graphql/queries/studentUser';

import '../../../styles.css'; // Include the CSS for resizer
import { useTranslation } from 'react-i18next';

const getProgressNumber = (value) => {
  if (value && typeof value === 'object') {
    const progress = Number(value.progress) || 0;
    const zoomprogress = Number(value.zoomprogress) || 0;
    return Number(value.certificateProgress ?? Math.max(progress, zoomprogress)) || 0;
  }

  return Number(value) || 0;
};

const API_BASE_URL = (
  process.env.REACT_APP_URL_PUBLIC ||
  (process.env.REACT_APP_GRAPHQL_SERVER || '').replace('/graphql', '')
).replace(/\/$/, '');

const pickFirstImportValue = (row, keys) => {
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key];
  }

  return '';
};

const normalizeStudentImportRow = (row) => {
  const firstname = pickFirstImportValue(row, ['firstname', 'firstName', 'First Name', 'ชื่อ', 'ชื่อจริง']);
  const lastname = pickFirstImportValue(row, ['lastname', 'lastName', 'Last Name', 'นามสกุล', 'สกุล']);
  const mobile = String(pickFirstImportValue(row, ['mobile', 'phone', 'tel', 'เบอร์', 'เบอร์โทร', 'เบอร์โทรศัพท์'])).trim();
  const email = String(pickFirstImportValue(row, ['email', 'Email', 'อีเมล', 'อีเมล์'])).trim();
  const username = String(pickFirstImportValue(row, ['username', 'Username', 'ชื่อผู้ใช้'])).trim() || email || mobile;

  return {
    firstname: String(firstname || '').trim(),
    lastname: String(lastname || '').trim(),
    mobile,
    email,
    username,
  };
};

const validateStudentImportRows = (rows, existingUsers = [], currentEnrollments = []) => {
  const seen = new Set();
  return rows.map((row, index) => {
    const username = row.username || row.email || row.mobile;
    const key = `${username}|${row.mobile}|${row.email}`;
    const existingUser = existingUsers.find(
      (user) =>
        (username && user.username === username) ||
        (row.mobile && user.mobile === row.mobile) ||
        (row.email && user.email === row.email)
    );
    const alreadyEnrolled = currentEnrollments.some((enrollment) => {
      const student = enrollment.student || {};
      return (
        (existingUser?._id && student._id === existingUser._id) ||
        (username && student.username === username) ||
        (row.mobile && student.mobile === row.mobile) ||
        (row.email && student.email === row.email)
      );
    });
    const errors = [];

    if (!row.firstname) errors.push('ไม่มี firstname');
    if (!row.lastname) errors.push('ไม่มี lastname');
    if (!row.mobile) errors.push('ไม่มี mobile');
    if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) errors.push('email ไม่ถูกต้อง');
    if (seen.has(key)) errors.push('ข้อมูลซ้ำในไฟล์');
    if (existingUser) errors.push('มีผู้ใช้นี้ในระบบแล้ว');
    if (alreadyEnrolled) errors.push('อยู่ในรายวิชานี้แล้ว');
    seen.add(key);

    return {
      ...row,
      username,
      rowNumber: row.rowNumber || index + 2,
      exists: !!existingUser,
      errors,
      ready: errors.length === 0,
    };
  });
};

const StudentListPanel = ({
  studentList, // This is studentofsubject data for a specific subject
  contentList,
  progressData,
  contentQuestionsMap,
  studentProcessedQuizAnswersMap, // Renamed prop
  studentSurveyAnswersMap, // Added prop
  isLoading,
  setStudent,
  currentSubjectId,
  onZoomProgressImported,
  onProgressCleared,
  onStudentsImported,
}) => {
  const bg = useColorModeValue('brand.100', 'brand.700');
  const tableBg = useColorModeValue('white', 'brand.800');
  const rowHoverBg = useColorModeValue('brand.50', 'brand.700');
  const interactiveHeaderColor = useColorModeValue('blue.600', 'blue.400'); // Moved hook call here
  const { t } = useTranslation();
  const toast = useToast();
  const zoomImportInputRef = useRef(null);
  const studentImportInputRef = useRef(null);
  const resetProgressCancelRef = useRef(null);
  const { isOpen: importPreviewOpen, onOpen: openImportPreview, onClose: closeImportPreview } = useDisclosure();
  const {
    isOpen: resetProgressOpen,
    onOpen: openResetProgress,
    onClose: closeResetProgress,
  } = useDisclosure();
  const [allStudentAnswers, setAllStudentAnswers] = useState([]);
  const [selectedContentForDetailView, setSelectedContentForDetailView] = useState(null);
  const [detailTableColumns, setDetailTableColumns] = useState([]);
  const [detailTableRows, setDetailTableRows] = useState([]);
  const [importZoomProgress, { loading: isImportingZoomProgress }] = useMutation(importZoomProgressMutation);
  const [removeProgress] = useMutation(removeProgressMutation);
  const [isImportingStudents, setIsImportingStudents] = useState(false);
  const [clearingProgressId, setClearingProgressId] = useState('');
  const [resetProgressTarget, setResetProgressTarget] = useState(null);
  const [studentImportPreviewRows, setStudentImportPreviewRows] = useState([]);

  const { data: studentUsersData } = useQuery(studentUsersQuery, {
    fetchPolicy: 'network-only',
  });

  const handleClearProgress = useCallback(
    async (progressId) => {
      if (!progressId || clearingProgressId) return;

      setClearingProgressId(progressId);
      try {
        const { data } = await removeProgress({ variables: { _id: progressId } });
        if (data?.removeProgress?.recordId) {
          toast({
            title: 'รีเซ็ตผลการทำเนื้อหาสำเร็จ',
            description: 'ลบ progress คำตอบหรือไฟล์ที่ส่ง และคะแนนของเนื้อหานี้แล้ว',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onProgressCleared?.();
        }
      } catch (error) {
        toast({
          title: 'รีเซ็ตผลการทำเนื้อหาไม่สำเร็จ',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setClearingProgressId('');
        setResetProgressTarget(null);
        closeResetProgress();
      }
    },
    [clearingProgressId, closeResetProgress, onProgressCleared, removeProgress, toast]
  );

  const requestClearProgress = useCallback(
    (progressId, content) => {
      if (!progressId || clearingProgressId) return;
      setResetProgressTarget({
        progressId,
        contentName: content?.name || 'เนื้อหานี้',
      });
      openResetProgress();
    },
    [clearingProgressId, openResetProgress]
  );

  // Define the click handler for content headers
  const handleContentHeaderClick = (content) => {
    const contentType = content.type; // Assuming content object has a 'type' property
    if (contentType === 'quiz' || contentType === 'survey') {
      if (selectedContentForDetailView && selectedContentForDetailView._id === content._id) {
        // If the same content header is clicked again, toggle off
        setSelectedContentForDetailView(null);
        setDetailTableColumns([]);
        setDetailTableRows([]);
      } else {
        setSelectedContentForDetailView(content);
        // Detail table data will be set by the useEffect hook
      }
    } else {
      // For other content types, maintain original behavior (e.g., logging)
      // And ensure any existing detail view is cleared
      setSelectedContentForDetailView(null);
      setDetailTableColumns([]);
      setDetailTableRows([]);
      console.log('Content header clicked (non-quiz/survey):', content.name, content._id);
      // Future: Implement navigation or other actions here
    }
  };

  // Fetch all student enrollments which should include answers
  // Adjust this query if studentofsubjectsQueries doesn't include answers or if a more specific query is needed.
  const { data: allEnrollmentsData, loading: enrollmentsLoading } = useQuery(studentofsubjectsQueries, {
    // Variables might be needed if the query is paginated or requires specific filters not related to a single subject
    // For example, if it's fetching all enrollments across all subjects.
    // If studentofsubjectsQueries is designed to fetch for a *specific* subject and you need *all* subjects,
    // you might need a different query (e.g., 'allStudentEnrollmentsWithAnswers')
    // For now, let's assume studentofsubjectsQueries can fetch all necessary data or data for the current subject.
    // If it fetches only for the current subject, the Excel export will only show answers for that subject.
    // To show all subjects' answers, a broader query is needed.
    variables: {
      subject: currentSubjectId,
      // student: null, // To get all students for the subject
      // Consider if this query needs to be modified or if another query is more appropriate
      // to fetch all answers for all students across relevant subjects.
    },
    fetchPolicy: 'network-only',
    skip: !currentSubjectId, // Or remove skip if you intend to fetch all answers regardless of current subject context
    onCompleted: (data) => {
      if (data && data.studentofsubjects) {
        // Filter out enrollments that don't have answers or are not relevant
        const relevantAnswers = data.studentofsubjects.filter(
          (enrollment) => enrollment.answers && enrollment.answers.length > 0
        );
        setAllStudentAnswers(relevantAnswers);
      }
    },
  });

  // Prepare columns for TanStack Table
  const columnHelper = useMemo(() => createColumnHelper(), []);

  const columns = useMemo(() => {
    const studentCol = columnHelper.accessor('student', {
      id: 'studentFullName',
      header: () => t('ManageEnrollSubjectPage.table_header_student_name', 'Student Name'),
      cell: ({ getValue }) => {
        const student = getValue() || {};
        return (
          `${student.firstname || ''} ${student.lastname || ''}`.trim() ||
          t('ManageEnrollSubjectPage.name_not_available', 'Name not available')
        );
      },
      size: 200,
    });

    const contentCols = contentList.map((content) =>
      columnHelper.display({
        id: content._id,
        header: () => (
          <Box
            as='span' // Using span for inline behavior within header, Box provides styling capabilities
            onClick={() => handleContentHeaderClick(content)}
            cursor='pointer'
            color={content.type === 'quiz' || content.type === 'survey' ? interactiveHeaderColor : undefined}
            _hover={{ textDecoration: 'underline' }}
            title={`Click to view details for ${content.name}`} // Tooltip for better UX
          >
            {content.name}
          </Box>
        ),
        cell: ({ row }) => {
          const studentId = row.original.student?._id;
          const progressMap = progressData?.get(studentId) || new Map();
          const val = progressMap.get(content._id);
          const progressId = val?._id || val?.original_item?._id;
          return (
            <HStack spacing={2} justify='space-between'>
              <Text as='span'>{val !== undefined ? `${getProgressNumber(val)}%` : '0%'}</Text>
              {progressId ? (
                <IconButton
                  aria-label='Reset progress and submission'
                  title='Reset progress and submission'
                  icon={<Trash2 size={14} />}
                  size='xs'
                  variant='ghost'
                  colorScheme='red'
                  isLoading={clearingProgressId === progressId}
                  onClick={(event) => {
                    event.stopPropagation();
                    requestClearProgress(progressId, content);
                  }}
                />
              ) : null}
            </HStack>
          );
        },
        size: 120,
      })
    );

    return [studentCol, ...contentCols];
  }, [contentList, progressData, t, interactiveHeaderColor, clearingProgressId, requestClearProgress]); // Added interactiveHeaderColor

  // Prepare data for table rows
  const data = useMemo(() => studentList || [], [studentList]);

  // useEffect for preparing data for the detail table
  useEffect(() => {
    if (!selectedContentForDetailView || !['quiz', 'survey'].includes(selectedContentForDetailView.type)) {
      setDetailTableColumns([]);
      setDetailTableRows([]);
      return;
    }

    const contentItem = selectedContentForDetailView;
    const contentId = contentItem._id;
    const contentType = contentItem.type;
    const questionsFromConfig = contentQuestionsMap?.get(contentId);

    if (!questionsFromConfig || questionsFromConfig.length === 0) {
      console.warn(
        `No questions configured for content: ${contentItem.name} (ID: ${contentId}). Cannot display detail table.`
      );
      setDetailTableColumns([]);
      setDetailTableRows([]);
      return;
    }

    const newDetailColumns = [];
    const headerInfoList = []; // To map columns to data points, similar to excel export

    // First column: Student Name
    newDetailColumns.push(
      columnHelper.accessor('studentName', {
        id: 'studentName',
        header: () => t('ManageEnrollSubjectPage.table_header_student_name', 'Student Name'),
        cell: (info) => info.getValue(),
        size: 200,
      })
    );

    // Dynamic columns for questions
    if (contentType === 'quiz') {
      questionsFromConfig.forEach((qDef, index) => {
        const headerText = qDef?.question || `${t('Unnamed Question')} ${index + 1}`;
        newDetailColumns.push(
          columnHelper.accessor(`q_${index}`, {
            // Unique accessor
            id: `q_${index}`,
            header: () => headerText,
            cell: (info) => info.getValue(),
            size: 150,
          })
        );
        headerInfoList.push({
          questionIndex: index,
          accessor: `q_${index}`,
          type: 'quiz',
        });
      });
    } else if (contentType === 'survey') {
      questionsFromConfig.forEach((qDef, index) => {
        // qDef is from surveyconfig.surveyQuestions.questions
        const questionId = qDef?.name; // This is the UUID-like ID
        const questionTitle = qDef?.title || t('Unnamed Survey Question');

        if (qDef?.type === 'matrix') {
          qDef.rows?.forEach((row, rowIndex) => {
            const headerText = `${questionTitle} - ${row.text || t('Unnamed Row')}`;
            const accessorId = `survey_${questionId}_row_${row.value}`;
            newDetailColumns.push(
              columnHelper.accessor(accessorId, {
                id: accessorId,
                header: () => headerText,
                cell: (info) => info.getValue(),
                size: 180,
              })
            );
            headerInfoList.push({
              questionId: questionId,
              rowId: row.value,
              accessor: accessorId,
              type: 'survey_matrix_row',
              matrixColumns: qDef.columns,
            });
          });
        } else {
          const accessorId = `survey_${questionId}`;
          newDetailColumns.push(
            columnHelper.accessor(accessorId, {
              id: accessorId,
              header: () => questionTitle,
              cell: (info) => info.getValue(),
              size: 150,
            })
          );
          headerInfoList.push({
            questionId: questionId,
            accessor: accessorId,
            type: 'survey_simple',
          });
        }
      });
    }
    setDetailTableColumns(newDetailColumns);

    // Prepare row data
    const newDetailRows = studentList.map((studentEnrollment) => {
      const student = studentEnrollment.student;
      const studentId = student?._id;
      const studentName = student
        ? `${student.firstname || ''} ${student.lastname || ''}`.trim()
        : t('ManageEnrollSubjectPage.name_not_available', 'Name not available');

      const rowData = { studentName };

      headerInfoList.forEach((hInfo) => {
        let cellAnswer = ''; // Initialize for each question/column

        if (hInfo.type === 'quiz') {
          if (studentId && studentProcessedQuizAnswersMap) {
            const processedAnswersForStudentMap = studentProcessedQuizAnswersMap.get(studentId);
            if (processedAnswersForStudentMap) {
              const answerDetailsForContent = processedAnswersForStudentMap.get(contentId);
              if (answerDetailsForContent && answerDetailsForContent[hInfo.questionIndex] !== undefined) {
                let answerValue = answerDetailsForContent[hInfo.questionIndex];
                // Handle standardized answer format (array of strings) or older object format
                if (Array.isArray(answerValue)) {
                  cellAnswer = answerValue.join(', ');
                } else if (
                  typeof answerValue === 'object' &&
                  answerValue !== null &&
                  answerValue.useranswer !== undefined
                ) {
                  // This case might be from an older structure or specific quiz types
                  cellAnswer = String(answerValue.useranswer || '');
                } else {
                  cellAnswer = String(answerValue || ''); // Fallback for direct primitive values
                }
              }
            }
          }
        } else if (hInfo.type === 'survey_simple' || hInfo.type === 'survey_matrix_row') {
          if (studentId && studentSurveyAnswersMap) {
            const surveyAnswersForStudentMap = studentSurveyAnswersMap.get(studentId);
            if (surveyAnswersForStudentMap) {
              const surveyResultObject = surveyAnswersForStudentMap.get(contentId); // This is surveySubmissionData.survey.surveyAnswers
              if (surveyResultObject) {
                if (hInfo.type === 'survey_simple') {
                  const simpleAnswer = surveyResultObject[hInfo.questionId]; // hInfo.questionId is qDef.name (UUID)
                  if (simpleAnswer !== undefined) {
                    if (Array.isArray(simpleAnswer)) {
                      cellAnswer = simpleAnswer.join(', ');
                    } else {
                      // Align with Excel export: directly stringify, arrays are pre-joined.
                      // Objects will become [object Object] if not an array.
                      cellAnswer = String(simpleAnswer || '');
                    }
                  }
                } else if (hInfo.type === 'survey_matrix_row') {
                  const matrixQuestionAnswers = surveyResultObject[hInfo.questionId]; // hInfo.questionId is matrix question's UUID
                  if (
                    matrixQuestionAnswers &&
                    typeof matrixQuestionAnswers === 'object' &&
                    matrixQuestionAnswers[hInfo.rowId] !== undefined
                  ) {
                    // hInfo.rowId is the 'value' of the matrix row
                    const columnValue = matrixQuestionAnswers[hInfo.rowId];
                    // Attempt to map columnValue to column.text for readability, similar to Excel export
                    const columnDefinition = hInfo.matrixColumns?.find((col) => col.value === columnValue);
                    cellAnswer = columnDefinition?.text || String(columnValue); // Fallback to raw value
                  }
                }
              }
            }
          }
        }

        rowData[hInfo.accessor] = String(cellAnswer); // Ensure it's a string for the table cell
      });
      return rowData;
    });
    setDetailTableRows(newDetailRows);
  }, [
    selectedContentForDetailView,
    studentList,
    contentQuestionsMap,
    studentProcessedQuizAnswersMap,
    studentSurveyAnswersMap, // Added to dependency array
  ]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: 'onChange',
  });

  if (isLoading || enrollmentsLoading) {
    return (
      <Box p={5} display='flex' justifyContent='center' alignItems='center' h='100vh'>
        Loading...
      </Box>
    );
  }

  if (!studentList?.length) {
    return (
      <Box p={5} bg={bg} borderRadius='md' boxShadow='sm' w='100%'>
        No students are currently enrolled in this subject.
      </Box>
    );
  }

  if (!contentList?.length) {
    return (
      <Box p={5} bg={bg} borderRadius='md' boxShadow='sm' w='100%'>
        No content available for this subject.
      </Box>
    );
  }

  const handleExport = () => {
    // Decide what studentAnswers to pass.
    // If you want answers for ALL subjects, pass allStudentAnswers.
    // If you only want answers for the CURRENT subject, filter allStudentAnswers by currentSubjectId.
    // The export function is designed to create sheets per subject found in the passed array.

    // Option 1: Pass all answers fetched (could be for multiple subjects if query was broad)
    let answersToExport = allStudentAnswers;

    // Option 2: Filter for current subject's answers if allStudentAnswers contains data for multiple subjects
    // and you only want to export the current one's answers along with progress.
    // This depends on whether the studentAnswers sheet should reflect only the current context or be more global.
    // For the request "create new table from each subject", passing all relevant answers is appropriate.
    // if (currentSubjectId) {
    //   answersToExport = allStudentAnswers.filter(ans => ans.subject?._id === currentSubjectId);
    // }

    // console.log('🚀 ~ handleExport ~ studentProcessedQuizAnswersMap:', studentSurveyAnswersMap);
    exportStudentProgressToExcel({
      studentList, // for progress sheet (specific to current subject)
      contentList, // for progress sheet (specific to current subject)
      progressData, // for progress sheet (specific to current subject)
      studentAnswers: answersToExport, // This is studentofsubject data, potentially for non-quiz answers or fallback
      contentQuestionsMap,
      studentProcessedQuizAnswersMap, // Pass the renamed map
      studentSurveyAnswersMap, // Pass the survey answers map
      t,
    });
  };

  const downloadStudentImportTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet([
      {
        firstname: 'Somchai',
        lastname: 'Jaidee',
        mobile: '0812345678',
        email: 'somchai@example.com',
        username: '',
      },
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');
    XLSX.writeFile(workbook, 'subject_student_import_template.xlsx');
  };

  const readZoomProgressWorkbook = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const workbook = XLSX.read(event.target.result, { type: 'array' });
          resolve(workbook);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

  const readWorkbook = readZoomProgressWorkbook;

  const parsePercent = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const normalized = String(value).replace('%', '').trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const pickFirstValue = (row, keys) => {
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key];
    }

    return '';
  };

  const handleImportZoomProgress = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const workbook = await readZoomProgressWorkbook(file);
      const sheetName = workbook.SheetNames.includes(ZOOM_IMPORT_SHEET_NAME)
        ? ZOOM_IMPORT_SHEET_NAME
        : workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      const records = rows
        .map((row, index) => {
          const studentId = row.studentId || row['Student ID'] || row['รหัสผู้เรียน'];
          const contentId = row.contentId || row['Content ID'] || row['รหัสวิดีโอ'];
          const currentZoomProgress = parsePercent(
            pickFirstValue(row, ['currentZoomProgress', 'Current Zoom Progress', 'Zoom Progress', 'zoomprogress'])
          );

          if (!studentId || !contentId || currentZoomProgress === null) return null;

          return {
            row: index + 2,
            studentId,
            contentId,
            currentZoomProgress,
          };
        })
        .filter(Boolean);

      if (records.length === 0) {
        toast({
          title: t('ManageEnrollSubjectPage.zoom_import_empty_title', 'No zoom progress found'),
          description: t(
            'ManageEnrollSubjectPage.zoom_import_empty_desc',
            'Fill the currentZoomProgress column in the Zoom Progress Import sheet, then import again.'
          ),
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const { data } = await importZoomProgress({ variables: { records } });
      const result = data?.importZoomProgress;

      toast({
        title: t('ManageEnrollSubjectPage.zoom_import_success_title', 'Zoom progress imported'),
        description: `${t('ManageEnrollSubjectPage.zoom_import_updated', 'Updated')} ${
          result?.updated || 0
        } ${t('ManageEnrollSubjectPage.zoom_import_rows', 'rows')}${
          result?.skipped ? `, ${result.skipped} skipped` : ''
        }`,
        status: result?.skipped ? 'warning' : 'success',
        duration: 6000,
        isClosable: true,
      });

      onZoomProgressImported?.();
    } catch (error) {
      toast({
        title: t('ManageEnrollSubjectPage.zoom_import_error_title', 'Import failed'),
        description: error.message,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      event.target.value = '';
    }
  };

  const handleImportStudentsToSubject = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!currentSubjectId) {
      toast({
        title: t('ManageEnrollSubjectPage.import_student_missing_subject_title', 'No subject selected'),
        description: t('ManageEnrollSubjectPage.import_student_missing_subject_desc', 'Open this import from a subject student list.'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      event.target.value = '';
      return;
    }

    try {
      const workbook = await readWorkbook(file);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
      const data = rows
        .map((row, index) => ({ ...normalizeStudentImportRow(row), rowNumber: index + 2 }))
        .filter((row) => row.firstname && row.lastname && row.mobile);

      if (data.length === 0) {
        toast({
          title: t('ManageEnrollSubjectPage.import_student_empty_title', 'No students found'),
          description: t(
            'ManageEnrollSubjectPage.import_student_empty_desc',
            'The file must include firstname, lastname, and mobile columns.'
          ),
          status: 'warning',
          duration: 6000,
          isClosable: true,
        });
        return;
      }

      setStudentImportPreviewRows(validateStudentImportRows(data, studentUsersData?.studentUsers || [], studentList || []));
      openImportPreview();
    } catch (error) {
      toast({
        title: t('ManageEnrollSubjectPage.import_student_error_title', 'Import failed'),
        description: error?.response?.data?.message || error.message,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      event.target.value = '';
    }
  };

  const updateStudentImportPreviewRow = (index, field, value) => {
    const nextRows = studentImportPreviewRows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row));
    setStudentImportPreviewRows(validateStudentImportRows(nextRows, studentUsersData?.studentUsers || [], studentList || []));
  };

  const handleConfirmImportStudentsToSubject = async () => {
    const invalidRows = studentImportPreviewRows.filter((row) => !row.ready);
    if (invalidRows.length > 0) {
      toast({
        title: 'ยังไม่สามารถส่งเข้าระบบได้',
        description: `พบข้อมูลไม่ถูกต้องหรือซ้ำ ${invalidRows.length} แถว กรุณาแก้ไฟล์ Excel แล้ว import เข้ามาใหม่`,
        status: 'warning',
        duration: 7000,
        isClosable: true,
      });
      return;
    }

    const data = studentImportPreviewRows.filter((row) => row.ready).map(({ rowNumber, exists, errors, ready, ...row }) => row);
    if (data.length === 0) {
      toast({
        title: 'ไม่มีรายการที่พร้อมสร้าง',
        description: 'กรุณาแก้ไฟล์ Excel แล้ว import เข้ามาใหม่',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsImportingStudents(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/generate/member/${currentSubjectId}/false/false`, { data });
      const created = response?.data?.created || 0;
      const skipped = response?.data?.skipped?.length || 0;

      toast({
        title: t('ManageEnrollSubjectPage.import_student_success_title', 'Students imported'),
        description: `${t('ManageEnrollSubjectPage.import_student_created', 'Processed')} ${created} ${t(
          'ManageEnrollSubjectPage.import_student_rows',
          'students'
        )}${skipped ? `, ${skipped} skipped` : ''}`,
        status: skipped ? 'warning' : 'success',
        duration: 7000,
        isClosable: true,
      });

      closeImportPreview();
      setStudentImportPreviewRows([]);
      onStudentsImported?.();
    } catch (error) {
      toast({
        title: t('ManageEnrollSubjectPage.import_student_error_title', 'Import failed'),
        description: error?.response?.data?.message || error.message,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    } finally {
      setIsImportingStudents(false);
    }
  };

  return (
    <>
      <AlertDialog
        isOpen={resetProgressOpen}
        leastDestructiveRef={resetProgressCancelRef}
        onClose={closeResetProgress}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              รีเซ็ตผลการทำเนื้อหา
            </AlertDialogHeader>
            <AlertDialogBody>
              ต้องการรีเซ็ต “{resetProgressTarget?.contentName}” หรือไม่? ระบบจะลบ progress คำตอบหรือไฟล์ที่ส่ง
              และคะแนนของผู้เรียนสำหรับเนื้อหานี้อย่างถาวร
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={resetProgressCancelRef} onClick={closeResetProgress}>
                ยกเลิก
              </Button>
              <Button
                colorScheme='red'
                ml={3}
                isLoading={clearingProgressId === resetProgressTarget?.progressId}
                onClick={() => handleClearProgress(resetProgressTarget?.progressId)}
              >
                ยืนยันรีเซ็ต
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      <Modal isOpen={importPreviewOpen} onClose={closeImportPreview} size='6xl'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>ตรวจสอบรายชื่อก่อนสร้าง/เพิ่มเข้ารายวิชา</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={3}>
              รายการที่พร้อมส่ง: {studentImportPreviewRows.filter((row) => row.ready).length} / {studentImportPreviewRows.length}
            </Text>
            {studentImportPreviewRows.some((row) => !row.ready) ? (
              <Text mb={3} color='red.600' fontSize='sm'>
                พบข้อมูลซ้ำหรือไม่ผ่านเงื่อนไขการสร้างผู้ใช้ ระบบจะไม่บันทึกทั้งไฟล์ กรุณาแก้ไขไฟล์ Excel แล้ว import ใหม่
              </Text>
            ) : null}
            <Box overflowX='auto'>
              <Table size='sm'>
                <Thead>
                  <Tr>
                    <Th>แถว</Th>
                    <Th>firstname</Th>
                    <Th>lastname</Th>
                    <Th>mobile</Th>
                    <Th>email</Th>
                    <Th>username</Th>
                    <Th>สถานะ</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {studentImportPreviewRows.map((row, index) => (
                    <Tr key={`${row.rowNumber}-${index}`} bg={row.ready ? 'green.50' : 'red.50'}>
                      <Td>{row.rowNumber}</Td>
                      <Td>
                        <Input size='sm' value={row.firstname} isReadOnly />
                      </Td>
                      <Td>
                        <Input size='sm' value={row.lastname} isReadOnly />
                      </Td>
                      <Td>
                        <Input size='sm' value={row.mobile} isReadOnly />
                      </Td>
                      <Td>
                        <Input size='sm' value={row.email} isReadOnly />
                      </Td>
                      <Td>
                        <Input size='sm' value={row.username} isReadOnly />
                      </Td>
                      <Td>
                        <Text color={row.ready ? 'green.700' : 'red.700'} fontSize='sm'>
                          {row.ready ? 'พร้อมสร้าง user และเพิ่มเข้ารายวิชา' : row.errors.join(', ')}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={closeImportPreview}>
              ยกเลิก
            </Button>
            <Button
              colorScheme='green'
              isLoading={isImportingStudents}
              isDisabled={studentImportPreviewRows.some((row) => !row.ready)}
              onClick={handleConfirmImportStudentsToSubject}
            >
              ยืนยันส่งเข้าระบบ
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Box p={5} bg={bg} borderRadius='md' boxShadow='sm' w='100%' h='100vh' fontSize='sm'>
      {!selectedContentForDetailView ? (
        <>
          <Flex justifyContent='space-between' alignItems='center' mb={4}>
            <Heading size='md'>{t('ManageEnrollSubjectPage.student_progress_table_title', 'Student Progress')}</Heading>
            <HStack>
              <input
                ref={zoomImportInputRef}
                type='file'
                accept='.xlsx,.xls'
                style={{ display: 'none' }}
                onChange={handleImportZoomProgress}
              />
              <input
                ref={studentImportInputRef}
                type='file'
                accept='.xlsx,.xls'
                style={{ display: 'none' }}
                onChange={handleImportStudentsToSubject}
              />
              <Button
                colorScheme='green'
                variant='ghost'
                onClick={downloadStudentImportTemplate}
                disabled={isLoading || enrollmentsLoading}
              >
                Student Template
              </Button>
              <Button
                colorScheme='green'
                onClick={() => studentImportInputRef.current?.click()}
                isLoading={isImportingStudents}
                disabled={isLoading || enrollmentsLoading}
              >
                Import Students
              </Button>
              <Button
                colorScheme='green'
                variant='outline'
                onClick={() => zoomImportInputRef.current?.click()}
                isLoading={isImportingZoomProgress}
                disabled={isLoading || enrollmentsLoading}
              >
                {t('ManageEnrollSubjectPage.import_zoom_excel', 'Import Zoom Excel')}
              </Button>
              <Button
                colorScheme='blue'
                onClick={handleExport}
                disabled={isLoading || enrollmentsLoading} // Updated to include general isLoading from parent
              >
                {t('ManageEnrollSubjectPage.export_excel', 'Export to Excel')}
              </Button>
            </HStack>
          </Flex>

          <Box overflowX='auto' maxW='100%'>
            <Box
              className='table'
              display='inline-block'
              border='1px solid'
              borderColor={tableBg === 'white' ? 'brand.200' : 'brand.600'}
              borderRadius='md'
              bg={tableBg}
            >
              {/* Table Header */}
              <Flex className='thead' bg='brand.300' userSelect='none'>
                {table.getHeaderGroups()[0].headers.map((header) => (
                  <Box
                    key={header.id}
                    className='th'
                    flex={header.getSize() / 100}
                    minW='80px'
                    maxW='300px'
                    px={2}
                    py={1}
                    borderRight='1px solid'
                    borderColor='brand.300'
                    whiteSpace='nowrap'
                    overflow='hidden'
                    textOverflow='ellipsis'
                    position='relative'
                    fontWeight='bold' // <-- ADD THIS LINE
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}

                    {/* Resizer */}
                    {header.column.getCanResize() && (
                      <Box
                        {...{
                          onMouseDown: header.getResizeHandler(),
                          onTouchStart: header.getResizeHandler(),
                        }}
                        className='resizer'
                        sx={{
                          cursor: 'col-resize',
                          position: 'absolute',
                          right: 0,
                          top: 0,
                          height: '100%',
                          width: '6px',
                          zIndex: 10,
                          touchAction: 'none',
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Flex>

              {/* Table Body */}
              {table.getRowModel().rows.map((row, idx) => (
                <Flex
                  key={row.id}
                  className='tr'
                  bg={idx % 2 === 0 ? 'white' : 'brand.100'}
                  _hover={{ bg: rowHoverBg }}
                  cursor='pointer'
                  onClick={() => setStudent?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Box
                      key={cell.id}
                      className='td'
                      flex={cell.column.getSize() / 100}
                      minW='80px'
                      maxW='300px'
                      px={2}
                      py={1}
                      borderRight='1px solid'
                      borderColor='brand.200'
                      whiteSpace='nowrap'
                      overflow='hidden'
                      textOverflow='ellipsis'
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Box>
                  ))}
                </Flex>
              ))}
            </Box>
          </Box>
        </>
      ) : null}

      {/* Conditionally render the Quiz/Survey Detail Table */}
      {selectedContentForDetailView && detailTableRows && detailTableRows.length > 0 && (
        <Box mt={6}>
          {' '}
          {/* Added margin top for spacing */}
          <IconButton
            aria-label='Back'
            icon={<ChevronLeft />}
            onClick={() => {
              setSelectedContentForDetailView(null);
              setDetailTableColumns([]);
              setDetailTableRows([]);
            }}
            variant='ghost'
            size='sm'
            colorScheme='gray'
            mb={4}
          />
          <QuizSurveyDetailTable
            contentName={selectedContentForDetailView.name}
            columns={detailTableColumns}
            data={detailTableRows}
          />
        </Box>
      )}
      </Box>
    </>
  );
};

export default StudentListPanel;
